import { Loader2 } from "lucide-react"
import { forwardRef, useImperativeHandle } from "react"
import { PaymentActionButtons } from "./components/PaymentActionButtons"
import { PaymentStatusMessage } from "./components/PaymentStatusMessage"
import { useStripeTerminal } from "./hooks/useStripeTerminal"
import type { PaymentCBProps } from "./types"

export interface PaymentCBRef {
  resetPaymentState: () => Promise<void>
}

export const PaymentCB = forwardRef<PaymentCBRef, PaymentCBProps>(
  (
    {
      amount,
      onPaymentSuccess,
      onPaymentFailed,
      onPaymentCancelled,
      onReset,
      className = "",
      simulate,
    },
    ref
  ) => {
    const {
      // État
      paymentStatus,
      paymentIntentId,
      errorMessage,
      isCancelling,

      // Actions
      processPayment,
      cancelPayment,
      handleRetry,
      resetPaymentState,
      // runDiagnostic, // TODO: Implémenter dans le hook

      // Données utiles
      isLoadingToken,
      createPaymentIntentMutation,
    } = useStripeTerminal({
      amount,
      onPaymentSuccess,
      onPaymentFailed,
      onPaymentCancelled,
      onReset,
      simulate,
    })

    // Exposer la fonction resetPaymentState à l'extérieur
    useImperativeHandle(
      ref,
      () => ({
        resetPaymentState,
      }),
      [resetPaymentState]
    )

    if (isLoadingToken) {
      return (
        <div className={`flex items-center justify-center p-8 ${className}`}>
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            <span>Initialisation de Stripe Terminal...</span>
          </div>
        </div>
      )
    }

    return (
      <div className={`space-y-4 ${className}`}>
        {/* Messages selon le statut */}
        <PaymentStatusMessage
          paymentStatus={paymentStatus}
          errorMessage={errorMessage}
          amount={amount}
          paymentIntentId={paymentIntentId}
          isCancelling={isCancelling}
          createPaymentIntentPending={createPaymentIntentMutation.isPending}
          createPaymentIntentError={createPaymentIntentMutation.error}
          onCancel={cancelPayment}
        />

        {/* Boutons d'action */}
        <PaymentActionButtons
          paymentStatus={paymentStatus}
          createPaymentIntentPending={createPaymentIntentMutation.isPending}
          isConnectionReady={
            !isLoadingToken && paymentStatus !== "initializing"
          }
          onProcessPayment={processPayment}
          onRetry={handleRetry}
        />

        {/* Bouton de diagnostic en cas d'erreur */}
        {/* TODO: Implémenter runDiagnostic dans le hook
        {paymentStatus === "failed" && import.meta.env.DEV && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <button
              onClick={runDiagnostic}
              className="text-sm text-yellow-700 hover:text-yellow-900 underline"
            >
              🔍 Lancer le diagnostic de connexion (console)
            </button>
          </div>
        )}
        */}
      </div>
    )
  }
)

PaymentCB.displayName = "PaymentCB"
